/**
 * The application controller: the one place input, engine, renderer and audio meet.
 *
 * Everything else is deliberately ignorant of everything else — `game/` knows no DOM,
 * `render/` never mutates state, `input/` emits normalised events. This file is where those
 * seams are stitched, so if behaviour looks wrong at the couch, it is almost certainly
 * here rather than in a module.
 *
 * Interaction model for the Command phase:
 *
 *   cursor  →  X opens the action menu  →  pick an action
 *                                            ├─ board-target actions arm targeting;
 *                                            │  legal hexes highlight, A confirms
 *                                            ├─ list actions (research / trade /
 *                                            │  megastructure) open a choice list
 *                                            └─ pass commits immediately
 *
 * B always steps back exactly one level, which is the property that makes a controller UI
 * feel trustworthy.
 */

import {
  ACTIONS, ALL_SEATS, DEFAULT_ROUND_SECONDS, PLAYER_NAMES_DEFAULT, RESOURCE_KINDS, TRADE_TAKE,
} from '../game/constants';
import {
  activeSeat, advancePhase, applyAction, createGame, pendingBidders, runUntilInput,
} from '../game/engine';
import { checkAction, effectiveJumpRange, log, straightIshPath } from '../game/actions';
import { eventTradeRatio } from '../game/events';
import { MEGASTRUCTURES } from '../game/constants';
import { TECHS, researchCost } from '../game/tech';
import {
  chooseAction, chooseAuctionBid, chooseContestBid, describeAction, makePlans, type AiPlan,
} from '../game/ai';
import {
  claimTargets, fleetsOf, isBlocked, playerOf, surveyTargets, systemsOwnedBy, terraformTargets,
  upgradeTargets,
} from '../game/queries';
import { distance, parseKey } from '../game/hex';
import { seedToWord } from '../game/rng';
import type {
  ActionKind, GameAction, GameNotice, GameState, HexKey, InputEvent, MegastructureKind,
  PlayerId, ResourceKind,
} from '../game/types';

import { InputRouter } from '../input/inputRouter';
import { audio } from '../audio/audio';
import { Camera } from '../render/camera';
import { BoardRenderer, emptyBoardView, stepCursor, type BoardView } from '../render/board';
import { Hud, emptyHudView, type HudView } from '../render/hud';
import { Overlays, emptyOverlayView, type OverlayView } from '../render/overlays';
import {
  LOBBY_SETTINGS, Screens, type LobbySeat, type LobbyView, type PauseItem, type PauseView,
} from '../render/screens';
import { ACTION_ORDER } from '../render/actionOrder';
import { RESOURCE_INK, INK } from '../render/palette';
import { drawChoice, moveChoice, selectedChoice, type ChoiceItem, type ChoiceState } from './choice';
import { postScore } from '../net/api';
import { markBooted } from '../pwa';

type Screen = 'title' | 'lobby' | 'help' | 'game' | 'results';

/** Actions whose target is a hex on the board. */
const TARGETED_ACTIONS: ReadonlySet<ActionKind> = new Set<ActionKind>([
  'survey', 'claim', 'upgrade', 'terraform', 'deployFleet', 'move',
]);

/** Actions resolved through a modal choice list. */
const LIST_ACTIONS: ReadonlySet<ActionKind> = new Set<ActionKind>([
  'research', 'trade', 'megastructure',
]);

/** How long an AI seat pauses before acting, so the table can follow along. */
const AI_THINK_MS = 520;
const AI_BID_MS = 780;

export class App {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;

  private readonly camera = new Camera();
  private readonly hud = new Hud();
  private readonly overlays = new Overlays();
  private readonly screens = new Screens();
  private board: BoardRenderer;

  private readonly input: InputRouter;

  private state: GameState | null = null;
  private plans = new Map<PlayerId, AiPlan>();
  private screen: Screen = 'title';
  /** Screen to return to when help is closed. */
  private helpReturn: Screen = 'title';
  private helpPage = 0;

  private boardView: BoardView = emptyBoardView();
  private hudView: HudView = emptyHudView();
  private overlayView: OverlayView = emptyOverlayView();
  private pauseView: PauseView = {
    index: 0,
    items: ['resume', 'help', 'audio', 'restart', 'quit'],
    audio: { master: 0.8, sfx: 0.9, music: 0.6, muted: false },
  };
  private paused = false;

  /** Lobby state. */
  private lobbySettingIndex = 0;
  private lobbySeed = 1;
  private lobbyRounds = 8;
  private lobbyDifficulty: GameState['settings']['aiDifficulty'] = 'standard';
  private lobbyClock = DEFAULT_ROUND_SECONDS;

  /** Per-seat board cursors. */
  private cursors: Record<PlayerId, HexKey> = { 0: '0,0', 1: '0,0', 2: '0,0', 3: '0,0' };

  /** Command-phase interaction. */
  private armed: ActionKind | null = null;
  private selectedFleet: string | null = null;
  private choice: ChoiceState | null = null;

  /** Pacing timers. */
  private aiTimer = 0;
  private aiBidTimer = 0;
  private phaseHold = 0;
  private resultProgress = 0;
  private clockRemaining = 0;

  private lastFrame = 0;
  private rafHandle = 0;
  private matchStart = 0;
  private scoreSubmitted = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D is unavailable — the game cannot start.');
    this.ctx = ctx;

    this.board = new BoardRenderer(this.lobbySeed);

    this.input = new InputRouter({
      onEvent: (event) => this.onInput(event),
      onFirstInput: () => audio.unlock(),
    });

    // A random default galaxy so the title screen is not always the same seed.
    this.lobbySeed = (Math.floor(Math.random() * 0x7ffffff) + 1) >>> 0;

    this.applyUrlParams();
  }

  /* ---------------------------------------------------------------- lifecycle */

  start(): void {
    this.input.start();
    this.resize();
    window.addEventListener('resize', this.resize);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.camera.snapToTarget();
    markBooted();

    this.lastFrame = performance.now();
    this.rafHandle = requestAnimationFrame(this.frame);
  }

  dispose(): void {
    cancelAnimationFrame(this.rafHandle);
    window.removeEventListener('resize', this.resize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.input.dispose();
    audio.dispose();
  }

  /** `?quick=1` starts a match immediately; `?help=1` opens the rules. Both are PWA shortcuts. */
  private applyUrlParams(): void {
    const params = new URLSearchParams(location.search);
    if (params.get('seed')) {
      const parsed = Number(params.get('seed'));
      if (Number.isFinite(parsed) && parsed > 0) this.lobbySeed = parsed >>> 0;
    }
    if (params.get('help') === '1') {
      this.screen = 'help';
      this.helpReturn = 'title';
    } else if (params.get('quick') === '1') {
      this.beginMatch();
    }
  }

  private readonly resize = (): void => {
    // Render at device resolution, lay out in CSS pixels. Cap DPR at 2 — beyond that the
    // extra pixels cost real frame time on a 4K TV and nobody can see the difference.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;

    this.canvas.width = Math.round(width * dpr);
    this.canvas.height = Math.round(height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.camera.resize(width, height, dpr, this.hud.insets(width, height, this.hudView));
    this.board.resize(width, height, dpr);
    this.camera.snapToTarget();
  };

  private readonly onVisibility = (): void => {
    // Pause on tab-out so a match does not silently burn its advisory clock.
    if (document.hidden && this.screen === 'game') this.paused = true;
  };

  private get width(): number {
    return this.canvas.clientWidth || window.innerWidth;
  }

  private get height(): number {
    return this.canvas.clientHeight || window.innerHeight;
  }

  /* --------------------------------------------------------------------- frame */

  private readonly frame = (now: number): void => {
    const dt = Math.min(now - this.lastFrame, 100);
    this.lastFrame = now;

    this.input.poll(now);
    this.camera.update(dt);
    this.board.update(dt);
    this.hud.update(dt);
    this.overlays.update(dt);
    this.screens.update(dt);

    if (this.screen === 'game' && !this.paused) this.tickGame(dt);
    if (this.screen === 'results') this.resultProgress = Math.min(1, this.resultProgress + dt / 1600);

    this.render();
    this.rafHandle = requestAnimationFrame(this.frame);
  };

  /** Drive automatic phases, AI turns, and the advisory clock. */
  private tickGame(dt: number): void {
    const state = this.state;
    if (!state) return;

    if (state.phase === 'gameOver') {
      this.enterResults();
      return;
    }

    // Advisory clock — informational unless Blitz is on.
    if (state.settings.roundSeconds > 0 && state.phase === 'command') {
      this.clockRemaining -= dt / 1000;
      this.hudView.clockSeconds = this.clockRemaining;
      if (state.settings.blitz && this.clockRemaining <= 0) {
        const seat = activeSeat(state);
        if (seat !== null) this.commit({ kind: 'pass', player: seat });
      }
    } else {
      this.hudView.clockSeconds = state.settings.roundSeconds > 0 ? this.clockRemaining : null;
    }

    // Non-interactive phases auto-advance after a short beat so the table can read them.
    if (this.phaseHold > 0) {
      this.phaseHold -= dt;
      return;
    }

    switch (state.phase) {
      case 'setup':
        advancePhase(state, this.notice);
        break;

      case 'drift':
        // Held until the player dismisses the event card.
        this.overlayView.eventProgress = Math.min(1, this.overlayView.eventProgress + dt / 380);
        break;

      case 'production':
      case 'upkeep':
        // Auto-advance; the visuals already fired via notices.
        this.phaseHold = 900;
        advancePhase(state, this.notice);
        break;

      case 'command': {
        const seat = activeSeat(state);
        if (seat === null) {
          advancePhase(state, this.notice);
          break;
        }
        if (playerOf(state, seat).kind === 'ai') {
          this.aiTimer += dt;
          if (this.aiTimer >= AI_THINK_MS) {
            this.aiTimer = 0;
            this.runAiTurn(seat);
          }
        }
        break;
      }

      case 'contest':
      case 'auction': {
        const bid = state.activeBid;
        if (!bid) {
          advancePhase(state, this.notice);
          break;
        }
        if (!bid.revealed) {
          this.aiBidTimer += dt;
          if (this.aiBidTimer >= AI_BID_MS) {
            this.aiBidTimer = 0;
            this.submitOneAiBid();
          }
        } else {
          this.overlayView.revealProgress = Math.min(1, this.overlayView.revealProgress + dt / 500);
        }
        break;
      }

      case 'finalScoring':
        this.enterResults();
        break;

      default:
        break;
    }
  }

  /**
   * Play one AI action, and narrate it.
   *
   * The action is chosen here rather than delegating to `stepAi` so the camera can look at
   * the target *before* the board changes, and so the fleet's origin hex is still known
   * when drawing a jump trail.
   */
  private runAiTurn(seat: PlayerId): void {
    const state = this.state;
    if (!state) return;

    const plan = this.plans.get(seat);
    if (!plan) {
      applyAction(state, { kind: 'pass', player: seat }, this.notice);
      this.afterAction();
      return;
    }

    const action = chooseAction(state, seat, plan);
    const focus = actionFocus(action);
    if (focus) {
      this.cursors[seat] = focus;
      this.camera.keepInView(parseKey(focus));
    }

    // Remember where the fleets were, so a move can be given a visible trail.
    const before = new Map(fleetsOf(state, seat).map((f) => [f.id, f.at]));

    log(state, seat, `${plan.name} ${describeAction(state, action)}.`);
    const legality = applyAction(state, action, this.notice);
    if (!legality.ok) {
      // The scorer proposed something the checker rejected. Never hang the round on it.
      applyAction(state, { kind: 'pass', player: seat }, this.notice);
    }

    for (const fleet of fleetsOf(state, seat)) {
      const from = before.get(fleet.id);
      if (from && from !== fleet.at) this.board.jump(this.camera, from, fleet.at, seat);
    }
    this.afterAction();
  }

  private submitOneAiBid(): void {
    const state = this.state;
    if (!state) return;
    const bid = state.activeBid;
    if (!bid || bid.revealed) return;

    for (const seat of pendingBidders(state)) {
      if (playerOf(state, seat).kind !== 'ai') continue;
      const plan = this.plans.get(seat);
      const amount = plan
        ? bid.kind === 'contest'
          ? chooseContestBid(state, seat, plan, bid.systemId!)
          : chooseAuctionBid(state, seat, plan)
        : 0;
      applyAction(state, { kind: 'submitBid', player: seat, amount }, this.notice);
      return; // one per beat, so the reveals stagger
    }
  }

  /* -------------------------------------------------------------------- notices */

  /** Engine → presentation bridge. Purely cosmetic; dropping a notice changes no outcome. */
  private readonly notice = (n: GameNotice): void => {
    audio.notice(n);
    const state = this.state;
    if (!state) return;

    switch (n.type) {
      case 'claimed':
        this.board.celebrate(
          this.camera, n.systemId, n.development === 'colony' ? 'upgrade' : 'claim', n.player,
        );
        this.camera.shake(n.development === 'colony' ? 5 : 3);
        break;

      case 'relic':
        this.board.particles.burstRelic(
          this.camera.hexKeyToScreen(this.cursors[n.player]).x,
          this.camera.hexKeyToScreen(this.cursors[n.player]).y,
        );
        this.toast(`${playerOf(state, n.player).name} recovers a relic`, n.player, 'good');
        break;

      case 'production': {
        const label = Hud.formatGain(n.gained);
        if (label) {
          // Spawn low inside the seat panel: the label drifts upward over its life, and
          // spawning above the panel sent it straight through the button-hint bar.
          const anchor = this.hud.seatPanelAnchor(n.player, this.width, this.height);
          this.board.particles.floatText(anchor.x, anchor.y + 46, label, INK.good);
        }
        break;
      }

      case 'illegal':
        this.toast(n.reason, n.player, 'bad');
        this.input.rumble(n.player, 0.35, 90);
        break;

      case 'phase':
        this.onPhaseChanged(n.phase);
        break;

      case 'bidReveal': {
        this.camera.shake(8);
        if (n.outcome.winner !== null) {
          const bid = state.activeBid;
          if (bid?.systemId) this.board.celebrate(this.camera, bid.systemId, 'contest', n.outcome.winner);
        }
        break;
      }

      case 'tech':
        this.toast(`${playerOf(state, n.player).name} researches ${TECHS[n.techId].name}`, n.player, 'good');
        break;

      case 'megastructure':
        this.toast(
          `${playerOf(state, n.player).name} completes a ${MEGASTRUCTURES[n.kind].name}`,
          n.player, 'good',
        );
        this.camera.shake(12);
        break;

      case 'gameOver':
        this.enterResults();
        break;

      default:
        break;
    }
  };

  private onPhaseChanged(phase: GameState['phase']): void {
    const state = this.state;
    if (!state) return;

    audio.setMusicPhase(phase, state.round, state.settings.totalRounds);
    this.overlayView.revealProgress = 0;
    this.armed = null;
    this.choice = null;
    this.hudView.menuOpen = false;
    this.boardView.dimNonTargets = false;
    this.boardView.targets.clear();

    switch (phase) {
      case 'drift':
        this.overlayView.eventProgress = 0;
        this.clockRemaining = state.settings.roundSeconds;
        this.banner(`Round ${state.round}`, seedToWord(state.settings.seed), null);
        break;
      case 'command': {
        const seat = activeSeat(state);
        if (seat !== null && playerOf(state, seat).kind === 'human') {
          this.banner(playerOf(state, seat).name, 'Command phase', seat);
        }
        this.syncCursorToOwn();
        break;
      }
      case 'contest':
        if (state.activeBid) {
          this.banner('Contest', 'Sealed Fuel bid', null);
          this.resetDrafts();
        }
        break;
      case 'auction':
        if (state.activeBid) {
          this.banner('Charter Auction', 'Sealed Data bid', null);
          this.resetDrafts();
        }
        break;
      case 'finalScoring':
        this.banner('Final Scoring', '', null);
        break;
      default:
        break;
    }
    this.hudView.showTechMarket = phase === 'command' || phase === 'production';
  }

  private resetDrafts(): void {
    for (const seat of ALL_SEATS) {
      this.overlayView.drafts[seat] = { amount: 0, locked: false };
    }
  }

  private toast(message: string, seat: PlayerId | null, tone: 'info' | 'bad' | 'good'): void {
    // `until` is on the HUD's own clock, which is what `drawToast` compares against.
    this.hudView.toast = { text: message, seat, tone, until: this.hudClock() + 2200 };
  }

  private banner(title: string, sub: string, seat: PlayerId | null): void {
    this.hudView.banner = { text: title, sub, seat, until: this.hudClock() + 1300 };
  }

  /** Deadlines for toasts and banners are on the HUD's clock, so read it from the HUD. */
  private hudClock(): number {
    return this.hud.now;
  }

  /* --------------------------------------------------------------------- input */

  private onInput(event: InputEvent): void {
    if (event.edge === 'up') return;

    // Any input is a user gesture; browsers need one before audio can start.
    audio.unlock();

    switch (this.screen) {
      case 'title': this.onTitleInput(event); break;
      case 'lobby': this.onLobbyInput(event); break;
      case 'help': this.onHelpInput(event); break;
      case 'game': this.onGameInput(event); break;
      case 'results': this.onResultsInput(event); break;
    }
  }

  private onTitleInput(event: InputEvent): void {
    if (event.button === 'a' || event.button === 'start') {
      audio.play('select');
      this.screen = 'lobby';
      this.input.setJoinEnabled(true);
    } else if (event.button === 'y') {
      this.helpReturn = 'title';
      this.helpPage = 0;
      this.screen = 'help';
      audio.play('menuOpen');
    }
  }

  private onLobbyInput(event: InputEvent): void {
    switch (event.button) {
      case 'start':
        audio.play('select');
        this.beginMatch();
        break;

      case 'b':
        // Leaving a seat is per-device; an unbound press falls back to the title screen.
        if (event.seat !== null) {
          this.input.releaseSeat(event.seat);
          audio.play('cancel');
        } else {
          this.screen = 'title';
          this.input.setJoinEnabled(false);
          audio.play('cancel');
        }
        break;

      case 'up':
        this.lobbySettingIndex =
          (this.lobbySettingIndex - 1 + LOBBY_SETTINGS.length) % LOBBY_SETTINGS.length;
        audio.play('cursorMove');
        break;
      case 'down':
        this.lobbySettingIndex = (this.lobbySettingIndex + 1) % LOBBY_SETTINGS.length;
        audio.play('cursorMove');
        break;
      case 'left':
        this.adjustLobbySetting(-1);
        break;
      case 'right':
      case 'x':
        this.adjustLobbySetting(1);
        break;
      case 'y':
        this.helpReturn = 'lobby';
        this.helpPage = 0;
        this.screen = 'help';
        break;
      default:
        break;
    }
  }

  private adjustLobbySetting(delta: number): void {
    const key = LOBBY_SETTINGS[this.lobbySettingIndex];
    switch (key) {
      case 'rounds':
        this.lobbyRounds = Math.max(4, Math.min(12, this.lobbyRounds + delta));
        break;
      case 'difficulty': {
        const order: GameState['settings']['aiDifficulty'][] = ['calm', 'standard', 'ruthless'];
        const index = order.indexOf(this.lobbyDifficulty);
        this.lobbyDifficulty = order[(index + delta + order.length) % order.length]!;
        break;
      }
      case 'seed':
        // Rerolling gives a whole new galaxy, so it is worth a full reseed rather than ±1.
        this.lobbySeed = ((this.lobbySeed + delta * 7919) >>> 0) || 1;
        break;
      case 'clock': {
        const steps = [0, 240, 300, 420, 600];
        const index = steps.indexOf(this.lobbyClock);
        const next = steps[(Math.max(0, index) + delta + steps.length) % steps.length]!;
        this.lobbyClock = next;
        break;
      }
      default:
        break;
    }
    audio.play('cursorMove');
  }

  private onHelpInput(event: InputEvent): void {
    switch (event.button) {
      case 'b':
      case 'y':
        this.screen = this.helpReturn;
        audio.play('menuClose');
        break;
      case 'rb':
      case 'right':
        this.helpPage++;
        audio.play('cursorMove');
        break;
      case 'lb':
      case 'left':
        this.helpPage--;
        audio.play('cursorMove');
        break;
      default:
        break;
    }
  }

  private onResultsInput(event: InputEvent): void {
    if (this.resultProgress < 0.9) {
      // Let A skip the reveal animation rather than doing nothing.
      if (event.button === 'a') this.resultProgress = 1;
      return;
    }
    if (event.button === 'a') {
      audio.play('select');
      this.beginMatch();
    } else if (event.button === 'b') {
      audio.play('cancel');
      this.state = null;
      this.screen = 'title';
      audio.stopMusic();
    }
  }

  /* ---------------------------------------------------------------- game input */

  private onGameInput(event: InputEvent): void {
    const state = this.state;
    if (!state) return;

    // Pause is global and always available.
    if (event.button === 'start') {
      this.paused = !this.paused;
      this.pauseView.index = 0;
      audio.play(this.paused ? 'menuOpen' : 'menuClose');
      return;
    }
    if (this.paused) {
      this.onPauseInput(event);
      return;
    }

    // Scoreboard is a modal read-only view.
    if (event.button === 'back') {
      this.overlayView.showScoreboard = !this.overlayView.showScoreboard;
      audio.play(this.overlayView.showScoreboard ? 'menuOpen' : 'menuClose');
      return;
    }
    if (this.overlayView.showScoreboard) {
      if (event.button === 'a' || event.button === 'b') {
        this.overlayView.showScoreboard = false;
        audio.play('menuClose');
      }
      return;
    }

    // Bid phases take priority: all four seats are live at once.
    if ((state.phase === 'contest' || state.phase === 'auction') && state.activeBid) {
      this.onBidInput(event);
      return;
    }

    // The event card gates the Drift phase.
    if (state.phase === 'drift') {
      if (event.button === 'a' && this.overlayView.eventProgress >= 0.9) {
        audio.play('select');
        advancePhase(state, this.notice);
      }
      return;
    }

    if (state.phase !== 'command') {
      if (event.button === 'a') advancePhase(state, this.notice);
      return;
    }

    // From here on we are in the Command phase and only the active seat may act.
    const seat = activeSeat(state);
    if (seat === null) return;
    if (event.seat !== null && event.seat !== seat) {
      // A non-active player pressing buttons is common at a couch; stay quiet about it
      // rather than buzzing, but let them look around with Y.
      if (event.button === 'y') this.toggleInspect(event.seat);
      return;
    }
    if (playerOf(state, seat).kind === 'ai') return;

    if (this.choice) {
      this.onChoiceInput(event, seat);
      return;
    }
    if (this.hudView.menuOpen) {
      this.onMenuInput(event, seat);
      return;
    }
    this.onBoardInput(event, seat);
  }

  private onPauseInput(event: InputEvent): void {
    const items = this.pauseView.items;
    switch (event.button) {
      case 'up':
        this.pauseView.index = (this.pauseView.index - 1 + items.length) % items.length;
        audio.play('cursorMove');
        break;
      case 'down':
        this.pauseView.index = (this.pauseView.index + 1) % items.length;
        audio.play('cursorMove');
        break;
      case 'lt':
      case 'rt': {
        if (items[this.pauseView.index] === 'audio') {
          const delta = event.button === 'rt' ? 0.1 : -0.1;
          this.pauseView.audio.master = Math.max(0, Math.min(1, this.pauseView.audio.master + delta));
          audio.setMaster(this.pauseView.audio.master);
          audio.play('cursorMove');
        }
        break;
      }
      case 'a':
        this.choosePauseItem(items[this.pauseView.index]!);
        break;
      case 'b':
        this.paused = false;
        audio.play('menuClose');
        break;
      default:
        break;
    }
  }

  private choosePauseItem(item: PauseItem): void {
    audio.play('select');
    switch (item) {
      case 'resume':
        this.paused = false;
        break;
      case 'help':
        this.helpReturn = 'game';
        this.helpPage = 0;
        this.screen = 'help';
        this.paused = false;
        break;
      case 'audio':
        this.pauseView.audio.muted = audio.toggleMute();
        break;
      case 'restart':
        this.paused = false;
        this.beginMatch();
        break;
      case 'quit':
        this.paused = false;
        this.state = null;
        this.screen = 'title';
        audio.stopMusic();
        break;
    }
  }

  /* ------------------------------------------------------------- board targeting */

  private onBoardInput(event: InputEvent, seat: PlayerId): void {
    const state = this.state!;

    switch (event.button) {
      case 'up': case 'down': case 'left': case 'right':
        this.moveCursor(seat, event.button);
        break;

      case 'x':
        this.hudView.menuOpen = true;
        this.hudView.menuIndex = 0;
        audio.play('menuOpen');
        break;

      case 'y':
        this.toggleInspect(seat);
        break;

      case 'a':
        if (this.armed) this.confirmTarget(seat);
        else {
          // A with nothing armed opens the menu too — the most forgiving default.
          this.hudView.menuOpen = true;
          this.hudView.menuIndex = 0;
          audio.play('menuOpen');
        }
        break;

      case 'b':
        if (this.armed) this.disarm();
        break;

      case 'lb':
        this.cycleCursor(seat, 'fleet');
        break;
      case 'rb':
        this.cycleCursor(seat, 'colony');
        break;

      case 'lt':
        this.camera.zoomBy(0.86);
        break;
      case 'rt':
        this.camera.zoomBy(1.16);
        break;

      default:
        break;
    }
    void state;
  }

  private moveCursor(seat: PlayerId, button: 'up' | 'down' | 'left' | 'right'): void {
    const state = this.state!;
    // Pointy-top hexes have no orthogonal north/south neighbour, so vertical input maps
    // to the two "upper" or "lower" diagonals, biased by the cursor's column parity to
    // keep repeated presses travelling in a straight visual line.
    const current = this.cursors[seat];
    const coord = parseKey(current);
    let direction: number;
    switch (button) {
      case 'right': direction = 0; break;
      case 'left': direction = 3; break;
      case 'up': direction = coord.q % 2 === 0 ? 2 : 1; break;
      case 'down': direction = coord.q % 2 === 0 ? 5 : 4; break;
    }

    const next = stepCursor(state, current, direction);
    if (next !== current) {
      this.cursors[seat] = next;
      this.camera.keepInView(parseKey(next));
      audio.play('cursorMove', { seat });
      this.refreshTargets(seat);
    }
  }

  /** LB/RB jump between the seat's own pieces — far faster than walking the cursor. */
  private cycleCursor(seat: PlayerId, kind: 'fleet' | 'colony'): void {
    const state = this.state!;
    const ids = kind === 'fleet'
      ? fleetsOf(state, seat).map((f) => f.at)
      : systemsOwnedBy(state, seat).map((s) => s.id);
    const unique = [...new Set(ids)].sort();
    if (unique.length === 0) return;

    const currentIndex = unique.indexOf(this.cursors[seat]);
    const next = unique[(currentIndex + 1) % unique.length]!;
    this.cursors[seat] = next;
    this.camera.focusOn(parseKey(next));
    audio.play('select', { seat });

    // Cycling fleets while a move is armed also selects that fleet.
    if (kind === 'fleet' && this.armed === 'move') {
      const fleet = fleetsOf(state, seat).find((f) => f.at === next);
      if (fleet) {
        this.selectedFleet = fleet.id;
        this.boardView.selectedFleet = fleet.id;
        this.refreshTargets(seat);
      }
    }
  }

  private toggleInspect(seat: PlayerId): void {
    const current = this.cursors[seat];
    this.overlayView.inspect = this.overlayView.inspect === current ? null : current;
    this.boardView.inspect = this.overlayView.inspect;
    audio.play(this.overlayView.inspect ? 'menuOpen' : 'menuClose');
  }

  /* ------------------------------------------------------------------ menu flow */

  private onMenuInput(event: InputEvent, seat: PlayerId): void {
    switch (event.button) {
      case 'up':
        this.hudView.menuIndex =
          (this.hudView.menuIndex - 1 + ACTION_ORDER.length) % ACTION_ORDER.length;
        audio.play('cursorMove', { seat });
        break;
      case 'down':
        this.hudView.menuIndex = (this.hudView.menuIndex + 1) % ACTION_ORDER.length;
        audio.play('cursorMove', { seat });
        break;
      case 'a':
        this.chooseAction(ACTION_ORDER[this.hudView.menuIndex]!, seat);
        break;
      case 'b':
      case 'x':
        this.hudView.menuOpen = false;
        audio.play('menuClose');
        break;
      default:
        break;
    }
  }

  private chooseAction(kind: ActionKind, seat: PlayerId): void {
    const state = this.state!;
    this.hudView.menuOpen = false;

    if (kind === 'pass') {
      audio.play('pass', { seat });
      this.commit({ kind: 'pass', player: seat });
      return;
    }

    if (LIST_ACTIONS.has(kind)) {
      this.choice = this.buildChoice(kind, seat);
      if (this.choice.items.length === 0) {
        this.choice = null;
        this.toast('Nothing available for that action.', seat, 'bad');
        audio.play('illegal');
        return;
      }
      audio.play('menuOpen', { seat });
      return;
    }

    if (TARGETED_ACTIONS.has(kind)) {
      this.armed = kind;
      this.boardView.dimNonTargets = true;

      // A Move's legal targets are relative to a *specific* fleet, so the fleet has to be
      // chosen before targets are computed — otherwise the set comes back empty and the
      // "no legal target" guard below disarms an action that was perfectly legal.
      this.selectedFleet = null;
      this.boardView.selectedFleet = null;
      if (kind === 'move') {
        const fleets = fleetsOf(state, seat);
        // Prefer a fleet already under the cursor; it is the one the player is looking at.
        const fleet = fleets.find((f) => f.at === this.cursors[seat]) ?? fleets[0];
        if (!fleet) {
          this.toast('You have no fleets to move.', seat, 'bad');
          audio.play('illegal');
          this.disarmQuietly();
          return;
        }
        this.selectedFleet = fleet.id;
        this.boardView.selectedFleet = fleet.id;
      }

      this.refreshTargets(seat);

      if (this.boardView.targets.size === 0) {
        this.toast(`No legal target for ${ACTIONS[kind].name}.`, seat, 'bad');
        audio.play('illegal');
        this.disarm();
        return;
      }

      // Park the cursor on a legal target so the player is never hunting for one.
      if (!this.boardView.targets.has(this.cursors[seat])) {
        const first = [...this.boardView.targets].sort()[0]!;
        this.cursors[seat] = first;
        this.camera.keepInView(parseKey(first));
        // Moving the cursor changes a Move's previewed path.
        if (kind === 'move') this.refreshTargets(seat);
      }
      audio.play('select', { seat });
    }
  }

  private disarm(): void {
    this.armed = null;
    this.selectedFleet = null;
    this.boardView.selectedFleet = null;
    this.boardView.dimNonTargets = false;
    this.boardView.targets.clear();
    this.boardView.reachable.clear();
    this.boardView.path = [];
    audio.play('cancel');
  }

  /** Recompute the highlight sets for the armed action. */
  private refreshTargets(seat: PlayerId): void {
    const state = this.state!;
    const targets = this.boardView.targets;
    targets.clear();
    this.boardView.reachable.clear();
    this.boardView.path = [];

    if (!this.armed) return;

    switch (this.armed) {
      case 'survey':
        for (const id of surveyTargets(state, seat)) targets.add(id);
        break;
      case 'claim':
        for (const id of claimTargets(state, seat)) targets.add(id);
        break;
      case 'upgrade':
        for (const id of upgradeTargets(state, seat)) targets.add(id);
        break;
      case 'terraform':
        for (const id of terraformTargets(state, seat)) targets.add(id);
        break;
      case 'deployFleet':
        for (const system of systemsOwnedBy(state, seat)) {
          if (system.development === 'colony') targets.add(system.id);
        }
        break;
      case 'move': {
        const fleet = this.selectedFleet ? state.fleets[this.selectedFleet] : undefined;
        if (!fleet) break;
        const range = effectiveJumpRange(state, seat);
        const from = parseKey(fleet.at);
        for (const system of Object.values(state.systems)) {
          const d = distance(from, system.coord);
          if (d === 0 || d > range) continue;
          this.boardView.reachable.set(system.id, d);
          targets.add(system.id);
        }
        // Preview the path to wherever the cursor currently is.
        const path = straightIshPath(state, fleet.at, this.cursors[seat], range);
        if (path) this.boardView.path = [fleet.at, ...path];
        break;
      }
      default:
        break;
    }
  }

  private confirmTarget(seat: PlayerId): void {
    const state = this.state!;
    const target = this.cursors[seat];
    if (!this.armed) return;

    if (!this.boardView.targets.has(target)) {
      this.toast('Not a legal target.', seat, 'bad');
      audio.play('illegal');
      return;
    }

    let action: GameAction;
    if (this.armed === 'move') {
      const fleet = this.selectedFleet ? state.fleets[this.selectedFleet] : undefined;
      if (!fleet) {
        this.toast('Pick a fleet with LB first.', seat, 'bad');
        return;
      }
      const path = straightIshPath(state, fleet.at, target, effectiveJumpRange(state, seat));
      if (!path) {
        this.toast('No route within range.', seat, 'bad');
        audio.play('illegal');
        return;
      }
      action = { kind: 'move', player: seat, fleetId: fleet.id, path };
    } else {
      action = { kind: this.armed, player: seat, systemId: target } as GameAction;
    }

    const from = this.armed === 'move' && this.selectedFleet
      ? state.fleets[this.selectedFleet]?.at
      : undefined;

    if (this.commit(action) && from) {
      this.board.jump(this.camera, from, target, seat);
    }
    this.disarmQuietly();
  }

  /** Clear targeting without the cancel sound (used after a successful commit). */
  private disarmQuietly(): void {
    this.armed = null;
    this.selectedFleet = null;
    this.boardView.selectedFleet = null;
    this.boardView.dimNonTargets = false;
    this.boardView.targets.clear();
    this.boardView.reachable.clear();
    this.boardView.path = [];
  }

  /* ---------------------------------------------------------------- choice flow */

  private onChoiceInput(event: InputEvent, seat: PlayerId): void {
    const choice = this.choice;
    if (!choice) return;

    switch (event.button) {
      case 'up':
        moveChoice(choice, -1);
        audio.play('cursorMove', { seat });
        break;
      case 'down':
        moveChoice(choice, 1);
        audio.play('cursorMove', { seat });
        break;
      case 'a': {
        const item = selectedChoice(choice);
        if (!item) break;
        if (!item.enabled) {
          audio.play('illegal');
          if (item.reason) this.toast(item.reason, seat, 'bad');
          break;
        }
        this.choice = null;
        this.commit(item.action);
        break;
      }
      case 'b':
        this.choice = null;
        audio.play('menuClose');
        break;
      default:
        break;
    }
  }

  private buildChoice(kind: ActionKind, seat: PlayerId): ChoiceState {
    const state = this.state!;
    const player = playerOf(state, seat);
    const items: ChoiceItem[] = [];

    if (kind === 'research') {
      for (const techId of state.techMarket) {
        const tech = TECHS[techId];
        const action: GameAction = { kind: 'research', player: seat, techId };
        const legality = checkAction(state, action);
        items.push({
          label: `${tech.name}  ·  T${tech.tier}`,
          detail: tech.blurb,
          badge: `◈ ${researchCost(player, techId)}`,
          badgeColor: RESOURCE_INK.data,
          action,
          enabled: legality.ok,
          reason: legality.ok ? undefined : legality.reason,
        });
      }
    } else if (kind === 'trade') {
      const [give, take] = eventTradeRatio(state);
      for (const from of RESOURCE_KINDS) {
        for (const to of RESOURCE_KINDS) {
          if (from === to) continue;
          const action: GameAction = { kind: 'trade', player: seat, give: from, take: to };
          const legality = checkAction(state, action);
          items.push({
            label: `${give} ${from}  →  ${take} ${to}`,
            detail: `You hold ${player.resources[from]} ${from}`,
            badge: legality.ok ? 'ok' : 'short',
            badgeColor: legality.ok ? INK.good : INK.bad,
            action,
            enabled: legality.ok,
            reason: legality.ok ? undefined : legality.reason,
          });
        }
      }
      void TRADE_TAKE;
    } else if (kind === 'megastructure') {
      // Requires both a kind and a colony to host it; offer kind × best colony.
      const colonies = systemsOwnedBy(state, seat).filter((s) => s.development === 'colony');
      const host = colonies.find((s) => s.megastructure === null);
      for (const megaKind of Object.keys(MEGASTRUCTURES) as MegastructureKind[]) {
        const def = MEGASTRUCTURES[megaKind];
        const action: GameAction = host
          ? { kind: 'megastructure', player: seat, systemId: host.id, pick: megaKind }
          : { kind: 'pass', player: seat };
        const legality = host ? checkAction(state, action) : { ok: false as const, reason: 'You need a Colony without a megastructure.' };
        items.push({
          label: def.name,
          detail: `${def.blurb}${host ? `  ·  at ${host.id}` : ''}`,
          badge: '8 VP',
          badgeColor: '#ffd76b',
          action,
          enabled: legality.ok,
          reason: legality.ok ? undefined : legality.reason,
        });
      }
    }

    const titles: Partial<Record<ActionKind, string>> = {
      research: 'Research — choose a technology',
      trade: 'Trade — choose an exchange',
      megastructure: 'Megastructure — choose a project',
    };
    return { title: titles[kind] ?? 'Choose', seat, items, index: 0 };
  }

  /* ----------------------------------------------------------------- bid input */

  private onBidInput(event: InputEvent): void {
    const state = this.state!;
    const bid = state.activeBid!;

    if (bid.revealed) {
      if (event.button === 'a') {
        audio.play('select');
        advancePhase(state, this.notice);
      }
      return;
    }

    const seat = event.seat;
    if (seat === null || !bid.participants.includes(seat)) return;
    if (bid.bids[seat] !== undefined) return;               // already locked
    if (playerOf(state, seat).kind !== 'human') return;

    const draft = this.overlayView.drafts[seat];
    const stock = playerOf(state, seat).resources[bid.resource];

    switch (event.button) {
      case 'rt':
      case 'up':
      case 'right':
        draft.amount = Math.min(stock, draft.amount + 1);
        audio.play('bidTick', { seat });
        break;
      case 'lt':
      case 'down':
      case 'left':
        draft.amount = Math.max(0, draft.amount - 1);
        audio.play('bidTick', { seat });
        break;
      case 'a':
        applyAction(state, { kind: 'submitBid', player: seat, amount: draft.amount }, this.notice);
        draft.locked = true;
        this.input.rumble(seat, 0.5, 120);
        break;
      case 'b':
        draft.amount = 0;
        audio.play('cancel', { seat });
        break;
      default:
        break;
    }
  }

  /* -------------------------------------------------------------------- commit */

  /** Apply an action, reporting whether it went through. */
  private commit(action: GameAction): boolean {
    const state = this.state;
    if (!state) return false;

    const legality = applyAction(state, action, this.notice);
    if (!legality.ok) return false;

    this.afterAction();
    return true;
  }

  private afterAction(): void {
    const state = this.state;
    if (!state) return;

    // Walk the machine forward through any phases that need no input.
    runUntilInput(state, this.notice, 40);

    const seat = activeSeat(state);
    if (seat !== null) {
      this.syncCursorToOwn();
      if (playerOf(state, seat).kind === 'human') {
        this.banner(playerOf(state, seat).name, 'Your action', seat);
      }
    }
  }

  /** Park the active seat's cursor somewhere useful when the baton arrives. */
  private syncCursorToOwn(): void {
    const state = this.state;
    if (!state) return;
    const seat = activeSeat(state);
    if (seat === null) return;

    const current = this.cursors[seat];
    if (state.systems[current]) {
      // Already somewhere valid — only re-centre if it is off screen.
      this.camera.keepInView(parseKey(current));
      return;
    }
    const fleet = fleetsOf(state, seat)[0];
    const home = systemsOwnedBy(state, seat)[0];
    const anchor = fleet?.at ?? home?.id ?? '0,0';
    this.cursors[seat] = anchor;
    this.camera.focusOn(parseKey(anchor));
  }

  /* --------------------------------------------------------------- match set-up */

  private beginMatch(): void {
    const bindings = this.input.bindings;
    const seats = ALL_SEATS.map((seat) => {
      const binding = bindings[seat];
      const claimed = binding?.source != null;
      return {
        kind: (claimed ? 'human' : 'ai') as 'human' | 'ai',
        name: claimed ? PLAYER_NAMES_DEFAULT[seat]! : `${PLAYER_NAMES_DEFAULT[seat]} (AI)`,
        gamepadIndex: binding?.source?.kind === 'gamepad' ? binding.source.index : null,
      };
    });

    this.state = createGame(
      {
        seed: this.lobbySeed,
        totalRounds: this.lobbyRounds,
        aiDifficulty: this.lobbyDifficulty,
        roundSeconds: this.lobbyClock,
        blitz: false,
      },
      seats,
    );
    this.plans = makePlans(this.state);
    this.board = new BoardRenderer(this.lobbySeed);
    this.board.resize(this.width, this.height, Math.min(window.devicePixelRatio || 1, 2));

    this.boardView = emptyBoardView();
    this.hudView = emptyHudView();
    this.overlayView = emptyOverlayView();
    this.screen = 'game';
    this.paused = false;
    this.armed = null;
    this.choice = null;
    this.resultProgress = 0;
    this.scoreSubmitted = false;
    this.matchStart = performance.now();
    this.clockRemaining = this.lobbyClock;

    // Cursors start on each seat's homeworld.
    for (const seat of ALL_SEATS) {
      const home = systemsOwnedBy(this.state, seat)[0];
      this.cursors[seat] = home?.id ?? '0,0';
    }

    this.input.setJoinEnabled(false);
    this.camera.frameBoard();
    this.camera.snapToTarget();

    audio.startMusic();
    advancePhase(this.state, this.notice);
  }

  private enterResults(): void {
    if (this.screen === 'results') return;
    const state = this.state;
    if (!state) return;

    this.screen = 'results';
    this.resultProgress = 0;
    audio.play(state.winner !== null ? 'victory' : 'scoreTick');
    audio.setMusicPhase('gameOver', state.round, state.settings.totalRounds);

    if (!this.scoreSubmitted) {
      this.scoreSubmitted = true;
      // Fire and forget: a failed submission must never interrupt the results screen.
      void postScore({
        seed: state.settings.seed,
        rounds: state.round,
        durationMs: Math.round(performance.now() - this.matchStart),
        winner: state.winner,
        players: state.players.map((p) => ({
          name: p.name.slice(0, 24),
          seat: p.id,
          vp: p.vp,
          kind: p.kind === 'ai' ? 'ai' : 'human',
        })),
      });
    }
  }

  /* --------------------------------------------------------------------- render */

  private render(): void {
    const { ctx } = this;
    const w = this.width;
    const h = this.height;

    this.camera.setInsets(this.hud.insets(w, h, this.hudView));

    switch (this.screen) {
      case 'title':
        this.renderBackdrop(ctx, w, h);
        this.screens.drawTitle(ctx, w, h);
        break;

      case 'lobby':
        this.renderBackdrop(ctx, w, h);
        this.screens.drawLobby(ctx, this.lobbyView(), w, h);
        break;

      case 'help':
        this.renderBackdrop(ctx, w, h);
        this.screens.drawHelp(ctx, this.helpPage, w, h);
        break;

      case 'game':
        this.renderGame(ctx, w, h);
        break;

      case 'results':
        this.renderGame(ctx, w, h, true);
        this.overlays.drawResults(ctx, this.state!, w, h, this.resultProgress);
        break;
    }
  }

  /** Starfield-only backdrop for the menus, so they share the game's world. */
  private renderBackdrop(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    ctx.save();
    ctx.fillStyle = '#07080f';
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
    // Slow drift gives the menus life without any assets.
    this.board.starfield.draw(ctx, performance.now() / 90, performance.now() / 260, 1, performance.now());
  }

  private renderGame(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    quiet = false,
  ): void {
    const state = this.state;
    if (!state) return;

    // Keep the move-path preview live as the cursor travels.
    const seat = activeSeat(state);
    if (seat !== null && this.armed === 'move') this.refreshTargets(seat);

    this.boardView.cursors = this.visibleCursors(state);
    this.boardView.flash = this.blockedSystems(state);

    this.board.draw(ctx, state, this.camera, this.boardView);
    this.board.drawRegionLabels(ctx, this.camera);

    if (quiet) return;

    this.hud.draw(ctx, state, this.hudView, w, h);

    if (this.overlayView.inspect) {
      this.overlays.drawInspect(
        ctx, state, this.overlayView.inspect,
        this.camera.hexKeyToScreen(this.overlayView.inspect), w, h,
      );
    }

    if (state.phase === 'drift') {
      this.overlays.drawEventCard(ctx, state, this.overlayView, w, h);
    }
    if ((state.phase === 'contest' || state.phase === 'auction') && state.activeBid) {
      this.overlays.drawBid(ctx, state, this.overlayView, w, h);
    }
    if (this.choice) {
      drawChoice(ctx, this.choice, w, h);
    }
    if (this.overlayView.showScoreboard) {
      this.overlays.drawScoreboard(ctx, state, w, h);
    }
    if (this.paused) {
      this.screens.drawPause(ctx, this.pauseView, w, h);
    }
  }

  /**
   * Which cursors to draw. During the Command phase only the active seat has one; during a
   * sealed bid every participating seat does, because they are all acting at once.
   */
  private visibleCursors(state: GameState): { seat: PlayerId; id: HexKey; dim?: boolean }[] {
    if ((state.phase === 'contest' || state.phase === 'auction') && state.activeBid) {
      const bid = state.activeBid;
      if (bid.systemId) {
        return bid.participants.map((seat) => ({ seat, id: bid.systemId!, dim: true }));
      }
      return [];
    }
    const seat = activeSeat(state);
    if (seat === null) return [];
    return [{ seat, id: this.cursors[seat] }];
  }

  /** Systems producing nothing this round, so the board can flag them. */
  private blockedSystems(state: GameState): Set<HexKey> {
    const out = new Set<HexKey>();
    for (const system of Object.values(state.systems)) {
      if (isBlocked(system, state.round)) out.add(system.id);
    }
    return out;
  }

  private lobbyView(): LobbyView {
    const bindings = this.input.bindings;
    const seats: LobbySeat[] = ALL_SEATS.map((seat) => {
      const binding = bindings[seat];
      const claimed = binding?.source != null;
      return {
        seat,
        status: claimed ? 'human' : 'ai',
        device: binding?.label ?? 'Autarch AI',
        connected: binding?.connected ?? true,
        name: PLAYER_NAMES_DEFAULT[seat]!,
      };
    });

    return {
      seats,
      settings: {
        seed: this.lobbySeed,
        totalRounds: this.lobbyRounds,
        roundSeconds: this.lobbyClock,
        blitz: false,
        aiDifficulty: this.lobbyDifficulty,
      },
      settingIndex: this.lobbySettingIndex,
      hasGamepad: this.input.hasGamepad,
    };
  }
}

/** The system an action concerns, for pointing the camera at it. */
function actionFocus(action: GameAction): HexKey | null {
  if ('systemId' in action) return action.systemId;
  if (action.kind === 'move') return action.path[action.path.length - 1] ?? null;
  return null;
}

export type { ResourceKind };
